'use client';

import { VStack, HStack, Box, Divider } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { Clipboard } from '../../components/clipboard';
import { Button } from '../../components/button';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Copy01Icon,
  Refresh04Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../components/icon-button';
import type { MfaMessageDto } from '@terra/shared-contracts-applications';
import { useState } from 'react';
import { EmailMessageBody } from './EmailMessageBody';
import { EmailRawSourceViewer } from './EmailRawSourceViewer';

type EmailDisplayProps = {
  emailAddress: string;
  disabled?: boolean;
  messages?: MfaMessageDto[];
  isLoadingMessages?: boolean;
  onFetchMessages?: () => void;
};

export function EmailDisplay({
  emailAddress,
  disabled = false,
  messages = [],
  isLoadingMessages = false,
  onFetchMessages,
}: EmailDisplayProps) {
  const t = useTranslations('designSystem.mfa.emailDisplay');
  const tMfa = useTranslations('designSystem.mfa');
  const [isMessagesExpanded, setIsMessagesExpanded] = useState(false);

  return (
    <VStack alignItems="stretch" gap={4}>
      <VStack alignItems="stretch" gap={3}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            {t('generatedEmailAddress')}
          </Text>
          {disabled && (
            <Text fontSize="sm" color="fg.muted">
              {tMfa('disabled')}
            </Text>
          )}
        </HStack>

        <Box
          p={4}
          borderRadius="lg"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.muted"
        >
          <HStack justifyContent="space-between" alignItems="center">
            <Text
              fontSize="lg"
              fontWeight="medium"
              fontFamily="mono"
              color={disabled ? 'fg.disabled' : 'fg.default'}
              wordBreak="break-all"
            >
              {disabled ? '••••••••••••••••••••' : emailAddress}
            </Text>

            <Clipboard.Root value={emailAddress}>
              <Clipboard.Trigger asChild>
                <IconButton
                  variant="outline"
                  size="sm"
                  disabled={!emailAddress || disabled}
                  aria-label={t('copyEmailAddress')}
                >
                  <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                    <Copy01Icon size={16} />
                  </Clipboard.Indicator>
                </IconButton>
              </Clipboard.Trigger>
            </Clipboard.Root>
          </HStack>
        </Box>

        <Text fontSize="xs" color="fg.muted" textAlign="center">
          {t('useEmailForMfa')}
        </Text>
      </VStack>

      {onFetchMessages && (
        <>
          <Divider />

          <VStack alignItems="stretch" gap={3}>
            <HStack justifyContent="space-between" alignItems="center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMessagesExpanded(!isMessagesExpanded)}
                aria-label={
                  isMessagesExpanded ? t('hideMessages') : t('showMessages')
                }
              >
                <HStack gap={2} alignItems="center">
                  <Text fontSize="sm" fontWeight="medium" color="fg.default">
                    {t('latestEmailMessages')}
                  </Text>
                  {isMessagesExpanded ? (
                    <ArrowUp01Icon size={16} />
                  ) : (
                    <ArrowDown01Icon size={16} />
                  )}
                </HStack>
              </Button>
              {isMessagesExpanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onFetchMessages}
                  disabled={isLoadingMessages || disabled}
                  aria-label={t('refreshMessages')}
                >
                  <Refresh04Icon
                    size={14}
                    style={{
                      animation: isLoadingMessages
                        ? 'spin 1s linear infinite'
                        : 'none',
                    }}
                  />
                  {t('refresh')}
                </Button>
              )}
            </HStack>

            {isMessagesExpanded && (
              <>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg="bg.muted"
                  borderWidth="1px"
                  borderColor="border.muted"
                  minHeight="120px"
                >
                  {isLoadingMessages ? (
                    <VStack
                      justifyContent="center"
                      alignItems="center"
                      height="100px"
                    >
                      <Text fontSize="sm" color="fg.muted">
                        {t('loadingMessages')}
                      </Text>
                    </VStack>
                  ) : messages.length === 0 ? (
                    <VStack
                      justifyContent="center"
                      alignItems="center"
                      height="100px"
                    >
                      <Text fontSize="sm" color="fg.muted" textAlign="center">
                        {t('noEmailMessages')}
                      </Text>
                    </VStack>
                  ) : (
                    <VStack alignItems="stretch" gap={3}>
                      {messages.slice(0, 3).map((message) => (
                        <Box
                          key={message.id}
                          p={3}
                          bg="bg.default"
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor="border.muted"
                        >
                          <VStack alignItems="stretch" gap={2}>
                            <VStack alignItems="flex-start" gap={0.5}>
                              <Text fontSize="xs" color="fg.muted">
                                {new Date(message.receivedAt).toLocaleString()}
                              </Text>
                              {message.from && (
                                <Text
                                  fontSize="xs"
                                  color="fg.muted"
                                  fontFamily="mono"
                                >
                                  {t('from', { sender: message.from })}
                                </Text>
                              )}
                            </VStack>
                            {/* SECURITY: Use EmailMessageBody / EmailRawSourceViewer only — never render message HTML. */}
                            <EmailMessageBody
                              content={message.content}
                              links={message.links}
                            />
                            <EmailRawSourceViewer
                              rawPlain={message.rawPlain}
                              rawHtml={message.rawHtml}
                            />
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>

                <Text fontSize="xs" color="fg.muted" textAlign="center">
                  {t('showingLatest')}
                </Text>
              </>
            )}
          </VStack>
        </>
      )}
    </VStack>
  );
}

export type { EmailDisplayProps };
